import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, CircularProgress, Alert, Pagination, Card, CardContent,
  TextField, Checkbox, Button, Grid, Box, IconButton, Collapse, Tooltip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { fetchAddEventUsers, setEventPagination } from '../../../Reducer/sampleUsersSlice';
import { saveAs } from 'file-saver';

const AddEventUserList = () => {
  const dispatch = useDispatch();
  const { eventUsers, eventPagination, status, error } = useSelector((state) => state.sampleUsers);

  const [filters, setFilters] = useState({
    user_name: '',
    user_email: '',
    event_name: '',
    country: '',
    industry: '',
    event_date: ''
  });

  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [allSelectedUsers, setAllSelectedUsers] = useState(new Map());
  const [showFilters, setShowFilters] = useState(false); // Filter toggle state

  useEffect(() => {
    try {
      dispatch(fetchAddEventUsers({ filters, pagination: eventPagination }));
    } catch (err) {
      console.error("Error fetching event users:", err);
    }
  }, [dispatch, eventPagination, eventPagination.currentPage, eventPagination.pageSize, filters]);

  const handleFilterChange = (event) => {
    setFilters({ ...filters, [event.target.name]: event.target.value });
  };

  const handleResetFilters = () => {
    setFilters({ user_name: '', user_email: '', event_name: '', country: '', industry: '', event_date: '' });
    dispatch(fetchAddEventUsers({ filters: {}, pagination: eventPagination }));
  };

  const handlePageChange = (event, newPage) => {
    dispatch(setEventPagination({ currentPage: newPage }));
  };

  const toggleSelection = (user) => {
    setSelectedUsers((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(user.id)) {
        newSelected.delete(user.id);
        setAllSelectedUsers((prev) => {
          const updatedMap = new Map(prev);
          updatedMap.delete(user.id);
          return updatedMap;
        });
      } else {
        newSelected.add(user.id);
        setAllSelectedUsers((prev) => new Map(prev).set(user.id, user));
      }
      return newSelected;
    });
  };

  const exportToCSV = () => {
    try {
      if (allSelectedUsers.size === 0) {
        alert('No records selected!');
        return;
      }

      const csvHeaders = [
        "User Name", "Email", "Event Name", "Event Date", "Country", "Industry", "Status", "CreatedAt",
        "UTM Source", "UTM Medium", "UTM Campaign", "UTM Campaign ID", "Adgroup", "Adgroup ID", "UTM Term", "Device"
      ];
      const csvRows = [
        csvHeaders.join(','),
        ...Array.from(allSelectedUsers.values()).map(user => [
          user.user_name, user.user_email, user.event_name,
          new Date(user.event_date).toLocaleDateString(), user.country, user.industry,
          user.status === 1 ? "Confirmed" : "Pending", user.createdAt,
          user.utm_source || '', user.utm_medium || '', user.utm_campaign || '', user.utm_campaignid || '',
          user.adgroup || '', user.adgroupid || '', user.utm_term || '', user.device || ''
        ].join(','))
      ].join('\n');

      const csvBlob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });
      saveAs(csvBlob, "selected_event_users.csv");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      alert("Failed to export CSV. Please try again.");
    }
  };

  return (
    <Card sx={{
      margin: 'auto',
      mt: 4,
      p: 3,
      borderRadius: 4,
      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.05)',
      background: 'linear-gradient(to bottom, #ffffff, #f9fafc)'
    }}>
      <CardContent>

        {/* Header with Filter Icon */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h5" gutterBottom sx={{
            fontWeight: 600,
            color: '#2d3748',
            borderBottom: '2px solid #1976d2',
            display: 'inline-block'
          }}>
            Event Users List
          </Typography>

          <IconButton
            onClick={() => setShowFilters(prev => !prev)}
            color="primary"
            sx={{
              backgroundColor: '#e3f2fd',
              '&:hover': { backgroundColor: '#bbdefb' }
            }}
          >
            <FilterListIcon />
          </IconButton>
        </Box>

        {/* Filter Section */}
        <Collapse in={showFilters}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.keys(filters).map((key) => (
              <Grid item xs={6} sm={4} key={key}>
                <TextField
                  label={key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  name={key}
                  type={key === 'event_date' ? 'date' : 'text'}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={key === 'event_date' ? { shrink: true } : {}}
                  value={filters[key]}
                  onChange={handleFilterChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'white',
                      '&:hover fieldset': {
                        borderColor: '#1976d2 !important'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#64748b',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
            ))}
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 1
            }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => dispatch(fetchAddEventUsers({ filters, pagination: eventPagination }))}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                  boxShadow: '0 2px 10px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
                    background: 'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleResetFilters}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'rgba(239, 68, 68, 0.04)'
                  }
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Collapse>

        {/* CSV Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: '#64748b', fontWeight: 500 }}>
            {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : ''}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={exportToCSV}
            disabled={selectedUsers.size === 0}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              boxShadow: '0 2px 10px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 14px rgba(76, 175, 80, 0.4)',
                background: 'linear-gradient(135deg, #43a047 0%, #5cb860 100%)'
              },
              transition: 'all 0.3s ease',
              '&:disabled': {
                opacity: 0.7
              }
            }}
          >
            Download CSV
          </Button>
        </Box>

        {/* Table */}
        {status === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#1976d2' }} />
          </Box>
        ) : eventUsers?.length > 0 ? (
          <TableContainer component={Paper} sx={{
            mt: 2,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.03)'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)',
                  '& th': {
                    color: 'white',
                    fontWeight: 600,
                    borderBottom: 'none'
                  }
                }}>
                  <TableCell sx={{ width: 60 }}>
                    <Checkbox
                      checked={selectedUsers.size === eventUsers.length}
                      indeterminate={selectedUsers.size > 0 && selectedUsers.size < eventUsers.length}
                      onChange={() => {
                        if (selectedUsers.size === eventUsers.length) {
                          setSelectedUsers(new Set());
                          setAllSelectedUsers(new Map());
                        } else {
                          const all = new Set(eventUsers.map(u => u.id));
                          const map = new Map(eventUsers.map(u => [u.id, u]));
                          setSelectedUsers(all);
                          setAllSelectedUsers(map);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>CreatedAt</TableCell>
                  <TableCell>UTM Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{
                      backgroundColor: selectedUsers.has(user.id) ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedUsers.has(user.id)}
                        onChange={() => toggleSelection(user)}
                      />
                    </TableCell>
                    <TableCell>{user.user_name}</TableCell>
                    <TableCell>{user.user_email}</TableCell>
                    <TableCell>{user.event_name}</TableCell>
                    <TableCell>{new Date(user.event_date).toLocaleDateString()}</TableCell>
                    <TableCell>{user.country}</TableCell>
                    <TableCell>{user.industry}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: user.status === 1 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                          color: user.status === 1 ? '#4caf50' : '#ff9800',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        {user.status === 1 ? "Confirmed" : "Pending"}
                      </Box>
                    </TableCell>
                    <TableCell>{user.createdAt}</TableCell>
                    <TableCell>
                      <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5, minWidth: '150px' }}>
                        {(() => {
                          let utmData = user.raw_utm;
                          if (typeof utmData === 'string') {
                            try {
                              utmData = JSON.parse(utmData);
                            } catch (e) {
                              console.error("Error parsing raw_utm:", e);
                              utmData = null;
                            }
                          }

                          if (utmData && typeof utmData === 'object') {
                            return Object.entries(utmData)
                              .filter(([_, v]) => v && v !== "" && v !== null && v !== undefined)
                              .map(([k, v]) => (
                                <div key={k}>
                                  <strong>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</strong> {v}
                                </div>
                              ));
                          }

                          return (
                            <>
                              {user.utm_source && <div><strong>Source:</strong> {user.utm_source}</div>}
                              {user.utm_medium && <div><strong>Medium:</strong> {user.utm_medium}</div>}
                              {user.utm_campaign && <div><strong>Campaign:</strong> {user.utm_campaign}</div>}
                              {user.utm_campaignid && <div><strong>Campaign ID:</strong> {user.utm_campaignid}</div>}
                              {user.adgroup && <div><strong>Adgroup:</strong> {user.adgroup}</div>}
                              {user.adgroupid && <div><strong>Adgroup ID:</strong> {user.adgroupid}</div>}
                              {user.utm_term && <div><strong>Term:</strong> {user.utm_term}</div>}
                              {user.device && <div><strong>Device:</strong> {user.device}</div>}
                            </>
                          );
                        })()}
                        {!user.utm_source && !user.utm_medium && !user.utm_campaign && !user.utm_campaignid && !user.adgroup && !user.adgroupid && !user.utm_term && !user.device && (!user.raw_utm || typeof user.raw_utm !== 'object') && <span>-</span>}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              No event users found matching your criteria
            </Typography>
          </Paper>
        )}

        {/* Pagination */}
        <Pagination
          count={eventPagination?.totalPages || 1}
          page={eventPagination?.currentPage || 1}
          onChange={handlePageChange}
          color="primary"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4
          }}
        />
      </CardContent>
    </Card>
  );
};

export default AddEventUserList;
